export const BASE_URL =
    process.env.REACT_APP_API_URL == 'http://localhost:8080/api'
        ? 'http://localhost:8080'
        : 'https://case-delivery.sentinellegal.co.uk'

export type OpticsView = 'accepted' | 'rejected'

export interface HealthCheck {
    ok: boolean
    latency_ms: number
    lenders?: number
    error?: string
}

export interface HealthResponse {
    success: boolean
    ok: boolean
    checks: { leadsDb: HealthCheck; funderDb: HealthCheck }
    environment: string
    uptime_seconds: number
    timestamp: string
}

export interface DailyPoint {
    day: string
    received: number
    accepted: number
    rejected: number
    agreements: number
}

export interface StatsResponse {
    success: boolean
    days: number
    /** Reporting baseline (datetime). Null when historical data is included. */
    baseline?: string | null
    totals: {
        received: number
        accepted: number
        accepted_agreements: number
        rejected: number
        rejected_agreements: number
        duplicates: number
        failed: number
        acceptance_rate: number | null
        test_deliveries: number
        test_rejections: number
    }
    by_status: Record<string, { count: number; agreements: number }>
    rejection_reasons: { category: string; count: number }[]
    daily: DailyPoint[]
}

export interface DeliveryRow {
    id: number
    outcome?: OpticsView
    delivery_id: string | null
    submission_reference: string | null
    consumer_client_ref: string | null
    consumer_email: string | null
    enquiry_count: number
    agreement_count: number
    is_test: number
    sender_name?: string | null
    created_at: string
    // accepted rows
    status?: string
    error?: string | null
    client_id?: number | null
    processed_at?: string | null
    client_name?: string
    // rejected rows
    reason?: string
    reason_category?: string
    s3_json_key?: string | null
}

export const REASON_LABELS: Record<string, string> = {
    invalid_data: 'Invalid data',
    lender_not_approved: 'Lender not approved',
    duplicate_client: 'Duplicate client',
    duplicate_claim: 'Duplicate claim',
    client_not_eligible: 'Client not eligible',
    missing_documents: 'Missing documents',
    document_download_failed: 'Document download failed',
    invalid_signature: 'Invalid signature',
    aml_check_failed: 'AML / identity check failed',
}

export const STATUS_LABELS: Record<string, string> = {
    received: 'Received',
    processed: 'Processed',
    docs_pending: 'Docs pending',
    duplicate: 'Duplicate',
    test_skipped: 'Test (skipped)',
    failed: 'Failed',
}

export const formatDateTime = (value: string | null | undefined): string => {
    if (!value) return '—'
    const parsed = new Date(value)
    if (isNaN(parsed.getTime())) return value
    return parsed.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

/** UK date only: 2023-05-11T00:00:00.000Z → 11/05/2023 */
export const formatDateUk = (value: string | null | undefined): string => {
    if (!value) return '—'
    const parsed = new Date(value)
    if (isNaN(parsed.getTime())) return value
    return parsed.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    })
}

export const formatTime = (value: string | null | undefined): string => {
    if (!value) return '—'
    const parsed = new Date(value)
    if (isNaN(parsed.getTime())) return value
    return parsed.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

export const formatDay = (value: string): string => {
    const parsed = new Date(`${value}T00:00:00`)
    if (isNaN(parsed.getTime())) return value
    return parsed.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })
}

// ---------------------------------------------------------------------------
// Filtered result-set summary (reporting)
// ---------------------------------------------------------------------------

export interface DeliveriesSummary {
    received: number
    accepted: number
    rejected: number
    failed: number
    duplicates: number
    test_skipped: number
    accepted_agreements: number
    rejected_agreements: number
    total_agreements: number
    acceptance_rate: number | null
}

export interface SummaryResponse {
    success: boolean
    view: string
    baseline?: string | null
    summary: DeliveriesSummary
    daily: DailyPoint[]
    range: { first_at: string | null; last_at: string | null; from: string | null; to: string | null }
}

export type ListFilter = 'all' | OpticsView

export interface OpticsFilters {
    view: ListFilter
    search: string
    dateFrom: string
    dateTo: string
    sort: 'newest' | 'oldest'
    includeTest: boolean
    includeHistorical: boolean
}

export const DEFAULT_OPTICS_FILTERS: OpticsFilters = {
    view: 'all',
    search: '',
    dateFrom: '',
    dateTo: '',
    sort: 'newest',
    includeTest: false,
    includeHistorical: false,
}

export const filtersEqual = (a: OpticsFilters, b: OpticsFilters): boolean =>
    a.view === b.view &&
    a.search.trim() === b.search.trim() &&
    a.dateFrom === b.dateFrom &&
    a.dateTo === b.dateTo &&
    a.sort === b.sort &&
    a.includeTest === b.includeTest &&
    a.includeHistorical === b.includeHistorical

// ---------------------------------------------------------------------------
// Date range presets (weeks run Monday to Sunday for reporting)
// ---------------------------------------------------------------------------

const toIsoDay = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

const addDays = (date: Date, days: number): Date => {
    const copy = new Date(date)
    copy.setDate(copy.getDate() + days)
    return copy
}

/** Monday of the week containing `date` (Sunday belongs to the week just ending). */
const startOfWeekMonday = (date: Date): Date => {
    const dayOfWeek = date.getDay()
    return addDays(date, dayOfWeek === 0 ? -6 : 1 - dayOfWeek)
}

export interface RangePreset {
    id: string
    label: string
    resolve: () => { dateFrom: string; dateTo: string }
}

export const RANGE_PRESETS: RangePreset[] = [
    {
        id: 'thisWeek',
        label: 'This week (Mon–Sun)',
        resolve: () => {
            const monday = startOfWeekMonday(new Date())
            return { dateFrom: toIsoDay(monday), dateTo: toIsoDay(addDays(monday, 6)) }
        },
    },
    {
        id: 'lastWeek',
        label: 'Last week (Mon–Sun)',
        resolve: () => {
            const monday = addDays(startOfWeekMonday(new Date()), -7)
            return { dateFrom: toIsoDay(monday), dateTo: toIsoDay(addDays(monday, 6)) }
        },
    },
    {
        id: 'last7',
        label: 'Last 7 days',
        resolve: () => ({ dateFrom: toIsoDay(addDays(new Date(), -6)), dateTo: toIsoDay(new Date()) }),
    },
    {
        id: 'last30',
        label: 'Last 30 days',
        resolve: () => ({ dateFrom: toIsoDay(addDays(new Date(), -29)), dateTo: toIsoDay(new Date()) }),
    },
    {
        id: 'thisMonth',
        label: 'This month',
        resolve: () => {
            const now = new Date()
            return { dateFrom: toIsoDay(new Date(now.getFullYear(), now.getMonth(), 1)), dateTo: toIsoDay(now) }
        },
    },
]

const VIEW_LABELS: Record<ListFilter, string> = {
    all: 'All deliveries',
    accepted: 'Accepted only',
    rejected: 'Rejected only',
}

/** Human-readable one-liner describing the applied filters, used in reports. */
export const describeFilters = (filters: OpticsFilters): string => {
    const parts: string[] = [VIEW_LABELS[filters.view] ?? filters.view]
    if (filters.dateFrom || filters.dateTo) {
        parts.push(`${filters.dateFrom || 'earliest'} to ${filters.dateTo || 'latest'}`)
    } else {
        parts.push('all dates')
    }
    if (filters.search.trim()) parts.push(`search "${filters.search.trim()}"`)
    parts.push(filters.includeTest ? 'including test deliveries' : 'excluding test deliveries')
    if (filters.includeHistorical) parts.push('including pre-reset testing history')
    return parts.join(' · ')
}

// ---------------------------------------------------------------------------
// CSV building (shared by the export modal and the filtered report)
// ---------------------------------------------------------------------------

export interface ExportField {
    key: string
    label: string
    defaultOn: boolean
    value: (row: DeliveryRow) => string | number | null | undefined
}

export const EXPORT_FIELDS: ExportField[] = [
    { key: 'created_at', label: 'Received at', defaultOn: true, value: (r) => r.created_at },
    {
        key: 'outcome',
        label: 'Outcome',
        defaultOn: true,
        value: (r) => (r.outcome === 'accepted' ? `accepted (${r.status ?? ''})` : 'rejected'),
    },
    { key: 'sender_name', label: 'Sender (API key)', defaultOn: true, value: (r) => r.sender_name },
    { key: 'consumer_client_ref', label: 'Client reference', defaultOn: true, value: (r) => r.consumer_client_ref },
    { key: 'consumer_email', label: 'Email', defaultOn: true, value: (r) => r.consumer_email },
    { key: 'client_name', label: 'Client name (accepted)', defaultOn: true, value: (r) => r.client_name },
    { key: 'agreement_count', label: 'Agreements', defaultOn: true, value: (r) => r.agreement_count },
    {
        key: 'reason_category',
        label: 'Rejection category',
        defaultOn: true,
        value: (r) => (r.reason_category ? REASON_LABELS[r.reason_category] ?? r.reason_category : null),
    },
    { key: 'reason', label: 'Rejection reasons (full detail)', defaultOn: true, value: (r) => r.reason },
    { key: 'delivery_id', label: 'Delivery id', defaultOn: false, value: (r) => r.delivery_id },
    {
        key: 'submission_reference',
        label: 'Submission reference',
        defaultOn: false,
        value: (r) => r.submission_reference,
    },
    {
        key: 'status',
        label: 'Processing status (accepted)',
        defaultOn: false,
        value: (r) => (r.status ? STATUS_LABELS[r.status] ?? r.status : null),
    },
    { key: 'processed_at', label: 'Processed at', defaultOn: false, value: (r) => r.processed_at },
    { key: 'client_id', label: 'Sentinel client id', defaultOn: false, value: (r) => r.client_id },
    { key: 'error', label: 'Processing error', defaultOn: false, value: (r) => r.error },
    { key: 'enquiry_count', label: 'Enquiries', defaultOn: false, value: (r) => r.enquiry_count },
    { key: 'is_test', label: 'Test flag', defaultOn: false, value: (r) => r.is_test },
]

export const csvCell = (value: string | number | null | undefined): string => {
    if (value == null) return ''
    const text = String(value)
    return /[",\n\r]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text
}

const csvRow = (cells: (string | number | null | undefined)[]): string => cells.map(csvCell).join(',')

export const buildRowsCsv = (rows: DeliveryRow[], fields: ExportField[]): string =>
    [csvRow(fields.map((f) => f.label)), ...rows.map((row) => csvRow(fields.map((f) => f.value(row))))].join('\r\n')

export const downloadCsvFile = (content: string, filename: string): void => {
    const blob = new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.click()
    URL.revokeObjectURL(url)
}

/**
 * Report CSV: a summary block (totals for the filtered window), a per-day
 * breakdown, then one line per delivery. Built so it can be sent straight to
 * the introducer as the period's delivery report.
 */
export const buildReportCsv = ({
    brand,
    filters,
    summary,
    daily,
    rows,
    fields,
    truncated = false,
}: {
    brand: string
    filters: OpticsFilters
    summary: DeliveriesSummary | null
    daily: DailyPoint[]
    rows: DeliveryRow[]
    fields: ExportField[]
    truncated?: boolean
}): string => {
    const lines: string[] = [
        csvRow([`${brand} API delivery report`]),
        csvRow(['Generated', formatDateTime(new Date().toISOString())]),
        csvRow(['Filters', describeFilters(filters)]),
    ]
    if (truncated) lines.push(csvRow(['Note', 'Row list capped at the 50,000-row export limit']))
    lines.push('')

    if (summary) {
        lines.push(
            csvRow(['Requests sent', summary.received]),
            csvRow(['Accepted', summary.accepted]),
            csvRow(['Rejected', summary.rejected]),
            csvRow(['Failed (internal)', summary.failed]),
            csvRow(['Duplicates', summary.duplicates]),
            csvRow(['Acceptance rate', summary.acceptance_rate == null ? 'n/a' : `${summary.acceptance_rate}%`]),
            csvRow(['Cases created (accepted agreements)', summary.accepted_agreements]),
            csvRow(['Agreements in rejected deliveries', summary.rejected_agreements]),
            ''
        )
    }

    if (daily.length) {
        lines.push(csvRow(['Day', 'Received', 'Accepted', 'Rejected', 'Agreements']))
        for (const point of daily) {
            lines.push(csvRow([point.day, point.received, point.accepted, point.rejected, point.agreements]))
        }
        lines.push('')
    }

    lines.push(buildRowsCsv(rows, fields))
    return lines.join('\r\n')
}

/**
 * Deep-copy a JSON value truncating very long strings (base64 signatures,
 * inlined documents) so the viewer stays responsive. Full values remain
 * available via the "show full values" toggle.
 */
export const truncateJsonDeep = (value: any, maxString = 300): any => {
    if (typeof value === 'string') {
        return value.length > maxString ? `${value.slice(0, maxString)}… [truncated ${value.length} chars]` : value
    }
    if (Array.isArray(value)) return value.map((v) => truncateJsonDeep(v, maxString))
    if (value && typeof value === 'object') {
        const out: Record<string, any> = {}
        for (const [k, v] of Object.entries(value)) out[k] = truncateJsonDeep(v, maxString)
        return out
    }
    return value
}

export const DOCUMENT_ROLE_LABELS: Record<string, string> = {
    cfa: 'CFA',
    loa: 'LOA',
    aof: 'AOF / Authority to act',
    loc: 'Letter of claim',
    clientCare: 'Client care letter',
    creditReport: 'Credit report PDF',
    aml: 'AML PDF',
}

/** Mirror of case-delivery-app classifyWebhookDocument for optics display. */
export const classifyPayloadDocument = (doc: any): string | null => {
    const type = String(doc?.type ?? '').toLowerCase()
    if (type === 'credit_search') return 'creditReport'
    if (type === 'aml') return 'aml'
    // FFJ sends explicit role types on agreements[].documents[].
    if (type === 'cfa' || type === 'loa' || type === 'aof' || type === 'loc') return type
    if (type === 'client_care' || type === 'clientcare') return 'clientCare'
    if (type !== 'tcs') return null
    const name = String(doc?.filename ?? '').toLowerCase()
    if (name.includes('conditional-fee') || name.includes('cfa')) return 'cfa'
    if (name.includes('letter-of-authority') || name.includes('loa')) return 'loa'
    if (name.includes('authority-to-act') || name.includes('aof')) return 'aof'
    if (name.includes('letter-of-claim') || name.includes('loc')) return 'loc'
    if (name.includes('client-care')) return 'clientCare'
    return null
}

export interface PayloadAgreement {
    enquiry_reference: string | null
    lender_name: string | null
    account_id: string | null
    account_type: string | null
    status: string | null
    start_date: string | null
    end_date: string | null
    original_amount: number | string | null
    balance: number | string | null
    monthly_payment_amount: number | string | null
    loan_term: number | string | null
    payments_made: number | string | null
}

export interface PayloadDocument {
    role: string | null
    type: string | null
    filename: string | null
    mime_type: string | null
    url: string | null
    source: string
    reference: string | null
    account_id?: string | null
    enquiry_reference?: string | null
    case_ref?: string | null
}

export interface DocumentFailure {
    role: string | null
    filename: string | null
    reason: string
    account_id?: string | null
}

/** Agreements from a FFJ envelope (normalised or flat shape). */
export const extractPayloadAgreements = (payload: any): PayloadAgreement[] => {
    if (!payload || typeof payload !== 'object') return []

    const fromEnquiry = (enquiryBlock: any): PayloadAgreement[] => {
        const enquiryRef = enquiryBlock?.enquiry?.reference ?? enquiryBlock?.reference ?? null
        const agreements = Array.isArray(enquiryBlock?.agreements) ? enquiryBlock.agreements : []
        return agreements.map((a: any) => ({
            enquiry_reference: enquiryRef,
            lender_name: a?.lender_name ?? null,
            account_id: a?.account_id != null ? String(a.account_id) : null,
            account_type: a?.account_type ?? null,
            status: a?.status ?? null,
            start_date: a?.start_date ?? null,
            end_date: a?.end_date ?? null,
            original_amount: a?.original_amount ?? null,
            balance: a?.balance ?? null,
            monthly_payment_amount: a?.monthly_payment_amount ?? null,
            loan_term: a?.loan_term ?? null,
            payments_made: a?.payments_made ?? null,
        }))
    }

    if (Array.isArray(payload.enquiries)) {
        return payload.enquiries.flatMap(fromEnquiry)
    }

    // Flat FFJ shape: singular enquiry + top-level agreements.
    if (payload.enquiry || Array.isArray(payload.agreements)) {
        return fromEnquiry({
            enquiry: payload.enquiry,
            agreements: payload.agreements,
        })
    }

    return []
}

/** Documents from agreements[].documents[], historical enquiry docs, top-level AML/credit, and client-level artefacts. */
export const extractPayloadDocuments = (payload: any): PayloadDocument[] => {
    if (!payload || typeof payload !== 'object') return []
    const docs: PayloadDocument[] = []

    const push = (
        doc: any,
        source: string,
        extras: {
            forcedRole?: string | null
            account_id?: string | null
            enquiry_reference?: string | null
        } = {}
    ) => {
        if (!doc || typeof doc !== 'object') return
        docs.push({
            role: extras.forcedRole ?? classifyPayloadDocument(doc),
            type: doc.type ?? null,
            filename: doc.filename ?? null,
            mime_type: doc.mime_type ?? null,
            url: typeof doc.url === 'string' && doc.url.trim() ? doc.url : null,
            source,
            reference: doc.reference ?? null,
            account_id: extras.account_id ?? null,
            enquiry_reference: extras.enquiry_reference ?? null,
        })
    }

    for (const enquiry of Array.isArray(payload.enquiries) ? payload.enquiries : []) {
        const enquiryRef = enquiry?.enquiry?.reference ?? null
        for (const agreement of Array.isArray(enquiry?.agreements) ? enquiry.agreements : []) {
            const accountId = agreement?.account_id != null ? String(agreement.account_id) : null
            for (const doc of Array.isArray(agreement?.documents) ? agreement.documents : []) {
                push(doc, 'agreement.documents', {
                    account_id: accountId,
                    enquiry_reference: enquiryRef,
                })
            }
        }
        // Historical payloads placed claim docs on enquiry.documents — still
        // show them in the drawer even though new deliveries reject that shape.
        for (const doc of Array.isArray(enquiry?.documents) ? enquiry.documents : []) {
            push(doc, 'enquiry.documents', { enquiry_reference: enquiryRef })
        }
    }

    // Flat shape: agreements at top level with nested documents.
    if (!Array.isArray(payload.enquiries) && Array.isArray(payload.agreements)) {
        const enquiryRef = payload.enquiry?.reference ?? null
        for (const agreement of payload.agreements) {
            const accountId = agreement?.account_id != null ? String(agreement.account_id) : null
            for (const doc of Array.isArray(agreement?.documents) ? agreement.documents : []) {
                push(doc, 'agreement.documents', {
                    account_id: accountId,
                    enquiry_reference: enquiryRef,
                })
            }
        }
    }

    for (const doc of Array.isArray(payload.documents) ? payload.documents : []) {
        push(doc, 'documents')
    }
    if (payload.credit_report_pdf != null) push(payload.credit_report_pdf, 'credit_report_pdf', { forcedRole: 'creditReport' })
    if (payload.aml_pdf != null) push(payload.aml_pdf, 'aml_pdf', { forcedRole: 'aml' })

    return docs
}

/**
 * Document download / store failures from rejection reasons or an accepted
 * delivery's processing error JSON.
 */
export const extractDocumentFailures = ({
    reasons,
    processingError,
}: {
    reasons?: string[] | null
    processingError?: string | null
}): DocumentFailure[] => {
    const failures: DocumentFailure[] = []

    if (processingError) {
        try {
            const parsed = JSON.parse(processingError)
            if (Array.isArray(parsed)) {
                for (const item of parsed) {
                    if (item && typeof item === 'object') {
                        failures.push({
                            role: item.role ?? null,
                            filename: item.filename ?? null,
                            reason: String(item.reason ?? 'unknown'),
                            account_id: item.account_id ?? null,
                        })
                    }
                }
            }
        } catch {
            /* not JSON — ignore */
        }
    }

    for (const reason of reasons ?? []) {
        const match = String(reason).match(/^document download failed:\s*([^:]+):\s*(.+)$/i)
        if (!match) continue
        const label = match[1].trim()
        const detail = match[2].trim()
        failures.push({
            role: DOCUMENT_ROLE_LABELS[label] ? label : null,
            filename: DOCUMENT_ROLE_LABELS[label] ? null : label,
            reason: detail,
        })
    }

    return failures
}

export const failureForDocument = (doc: PayloadDocument, failures: DocumentFailure[]): DocumentFailure | null => {
    if (!failures.length) return null
    return (
        failures.find((f) => {
            const roleMatch =
                (doc.role && f.role === doc.role) ||
                (doc.filename && f.filename && doc.filename === f.filename) ||
                (doc.filename && f.filename && String(doc.filename).includes(String(f.filename))) ||
                (doc.role && f.filename === doc.role)
            if (!roleMatch) return false
            if (doc.account_id && f.account_id) return String(doc.account_id) === String(f.account_id)
            return true
        }) ?? null
    )
}

// ---------------------------------------------------------------------------
// Linked Funding
// ---------------------------------------------------------------------------

export type LinkedFundingStatus = 'in_batch' | 'audit_removed' | 'never_batched'

export const LINKED_STATUS_LABELS: Record<LinkedFundingStatus, string> = {
    in_batch: 'In funding batch',
    audit_removed: 'Audit-removed',
    never_batched: 'Never batched',
}

export const REMOVAL_REASON_LABELS: Record<string, string> = {
    'audit-check': 'Audit check (not in funder file)',
    'loan-start-date-removal': 'Loan start date (Oct 2024+)',
    'various-issues': 'Various issues',
    'removal-by-client-email': 'Removal by email',
    'excessive-claims-removal': 'More than 9 claims (auditor limit)',
}

export interface LinkedFundingFilters {
    search: string
    dateFrom: string
    dateTo: string
    ur: string
    batchGroup: string
    status: '' | LinkedFundingStatus
    removalReason: string
    highClaimCount: boolean
    includeTest: boolean
    sort: 'newest' | 'oldest'
}

export const DEFAULT_LINKED_FILTERS: LinkedFundingFilters = {
    search: '',
    dateFrom: '',
    dateTo: '',
    ur: '',
    batchGroup: '',
    status: '',
    removalReason: '',
    highClaimCount: false,
    includeTest: false,
    sort: 'newest',
}

export const linkedFiltersEqual = (a: LinkedFundingFilters, b: LinkedFundingFilters): boolean =>
    a.search.trim() === b.search.trim() &&
    a.dateFrom === b.dateFrom &&
    a.dateTo === b.dateTo &&
    a.ur === b.ur &&
    a.batchGroup === b.batchGroup &&
    a.status === b.status &&
    a.removalReason === b.removalReason &&
    a.highClaimCount === b.highClaimCount &&
    a.includeTest === b.includeTest &&
    a.sort === b.sort

export interface LinkedFundingClientRow {
    client_id: number
    client_name: string | null
    email: string | null
    consumer_client_ref: string | null
    first_received_at: string | null
    introducer_claim_count: number
    funder_claim_count: number
    active_claims: number
    removed_claims: number
    status: LinkedFundingStatus
    batch_groups: string[]
    nera_ids: string[]
    primary_removal_reason: string | null
    primary_removal_label: string
    latest_removal_date: string | null
}

export interface LinkedFundingTotals {
    accepted_api_clients: number
    in_batch: number
    audit_removed: number
    never_batched: number
    never_batched_eligible: number
    placement_rate: number | null
    still_active_rate: number | null
}

export interface LinkedFundingSummaryResponse {
    success: boolean
    introducer: string
    summary: {
        totals: LinkedFundingTotals
        removal_reasons: { reason: string | null; label: string; count: number }[]
        excessive_claims: { removed_clients: number; removed_claims: number }
        high_claim_clients: {
            threshold: number
            total: number
            in_batch: number
            audit_removed: number
            never_batched: number
            removed_for_excessive: number
        }
    }
    by_ur: {
        ur: string | null
        batch_group: string | null
        batches: number
        clients: number
        in_batch: number
        audit_removed: number
    }[]
    by_batch: {
        batch_group: string
        ur: string | null
        clients: number
        in_batch: number
        audit_removed: number
    }[]
    daily: {
        day: string
        accepted: number
        in_batch: number
        audit_removed: number
        never_batched: number
    }[]
    options: {
        urs: string[]
        batches: { batch_group: string; ur: string | null }[]
    }
}

export type LinkedFundingExportScope = 'accepted' | LinkedFundingStatus | 'never_batched_eligible'

export const LINKED_EXPORT_SCOPE_LABELS: Record<LinkedFundingExportScope, string> = {
    accepted: 'Accepted API clients',
    in_batch: 'In a funding batch',
    audit_removed: 'Audit-removed',
    never_batched: 'Never batched',
    never_batched_eligible: 'Never batched — eligible',
}

/** Client-only columns shared by every linked-funding metric download. */
export const LINKED_EXPORT_COLUMNS: { key: string; label: string }[] = [
    { key: 'client_id', label: 'Sentinel Client ID' },
    { key: 'consumer_client_ref', label: 'Introducer ID' },
    { key: 'title', label: 'Title' },
    { key: 'firstname', label: 'First Name' },
    { key: 'lastname', label: 'Last Name' },
    { key: 'client_name', label: 'Full Name' },
    { key: 'email', label: 'Email' },
    { key: 'telephone', label: 'Telephone' },
    { key: 'dob', label: 'Date of Birth' },
    { key: 'address', label: 'Address' },
    { key: 'flat', label: 'Flat' },
    { key: 'house_name', label: 'House Name' },
    { key: 'house_number', label: 'House Number' },
    { key: 'street', label: 'Street' },
    { key: 'address_line_one', label: 'Address Line 1' },
    { key: 'address_line_two', label: 'Address Line 2' },
    { key: 'town_city', label: 'Town / City' },
    { key: 'county', label: 'County' },
    { key: 'postcode', label: 'Postcode' },
    { key: 'client_created_at', label: 'Client Created At' },
    { key: 'first_received_at', label: 'First Received At' },
    { key: 'last_received_at', label: 'Last Received At' },
]

/** Extra columns for never-batched / audit-removed downloads only. */
export const LINKED_EXPORT_ELIGIBILITY_COLUMNS: { key: string; label: string }[] = [
    { key: 'status_label', label: 'Status' },
    { key: 'funding_outcome_explanation', label: 'Why Not Funded / Outcome' },
    { key: 'fundable', label: 'Currently Fundable' },
    { key: 'eligibility_reason', label: 'Eligibility Reason Code' },
    { key: 'eligibility_reason_label', label: 'Eligibility Reason' },
    { key: 'claim_failure_labels', label: 'Claim Failure Reasons' },
    { key: 'claim_failure_reasons', label: 'Claim Failure Reason Codes' },
    { key: 'primary_removal_reason', label: 'Audit Removal Reason Code' },
    { key: 'primary_removal_label', label: 'Audit Removal Reason' },
    { key: 'latest_removal_date', label: 'Latest Removal Date' },
    { key: 'signed', label: 'Signed' },
    { key: 'best_cfa_date', label: 'CFA / eSign Date' },
    { key: 'eligible_claim_count', label: 'Eligible Claim Count' },
    { key: 'total_claims_evaluated', label: 'Claims Evaluated' },
    { key: 'client_total_value', label: 'Client Quantum' },
]

export const linkedExportColumnsForScope = (
    scope: LinkedFundingExportScope
): { key: string; label: string }[] =>
    scope === 'never_batched' || scope === 'never_batched_eligible' || scope === 'audit_removed'
        ? [...LINKED_EXPORT_COLUMNS, ...LINKED_EXPORT_ELIGIBILITY_COLUMNS]
        : LINKED_EXPORT_COLUMNS

export const buildLinkedExportCsv = (
    rows: Record<string, unknown>[],
    scope: LinkedFundingExportScope = 'accepted'
): string => {
    const columns = linkedExportColumnsForScope(scope)
    const header = csvRow(columns.map((c) => c.label))
    const body = rows.map((row) =>
        csvRow(columns.map((c) => row[c.key] as string | number | null | undefined))
    )
    return [header, ...body].join('\r\n')
}
